<script>
import VideoPlayer from '@components/videos/video-player.vue';
import { authComputed, mediaComputed, mediaMethods } from '@state/helpers';
import { counterMixin } from '@mixins/counter-mixin';

export default {
  components: {
    VideoPlayer,
  },
  props: {
    videoSrc: {
      type: String,
      required: false,
      default: null,
    },
    existingPhoto: {
      type: String,
      required: false,
      default: null,
    },
    helperText: {
      type: String,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    requestType: {
      type: String,
      required: false,
      default: 'orgs',
    },
    hideButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    newVideoType: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      placeholder: require('@assets/images/placeholder-video-upload.svg'),
      videoPreview: null,
      videoType: null,
      videoLength: null,
      videoError: null,
      timeRemaining: 0,
      timeStarted: null,
      timeInterval: null,
    };
  },
  mixins: [counterMixin],
  computed: {
    ...authComputed,
    ...mediaComputed,
    currentUploadProgress() {
      const scopedUpload = this.signedPostData.find((upload) => {
        return (
          upload.type === 'video' && upload.requestType === this.requestType
        );
      });

      return scopedUpload ? scopedUpload.uploadProgress : 0;
    },
    currentUploadSuccess() {
      const scopedUpload = this.signedPostData.find((upload) => {
        return (
          upload.type === 'video' && upload.requestType === this.requestType
        );
      });

      return scopedUpload ? scopedUpload.success : false;
    },
    currentUploadIndex() {
      const scopedUpload = this.signedPostData.findIndex((upload) => {
        return (
          upload.type === 'video' && upload.requestType === this.requestType
        );
      });
      return scopedUpload;
    },
    currentUploadData() {
      return this.signedPostData.find((upload) => {
        return upload.type === 'video';
      });
    },
    videoOpts() {
      return {
        autoplay: false,
        controls: true,
        poster: this.existingPhoto ? this.existingPhoto : this.placeholder,
        fluid: true,
        responsive: true,
        sources: [
          {
            src: this.videoSrc,
            type: this.videoType || this.newVideoType,
          },
        ],
      };
    },
    styling() {
      return {
        width: this.currentUploadProgress + '%',
        'background-color': 'rgba($color-primary-one-wo-rgb, 0.7)',
      };
    },
    duration() {
      const seconds = this.timeRemaining;
      const unit = this.$_getTimeUnit(seconds);
      let result =
        seconds > 0 ? `${(seconds / 60).toFixed(2)} ` + unit : '0' + ' seconds';
      if (seconds < 60 && seconds > 0) {
        result = `${seconds.toFixed(0)} ` + unit;
      }
      return result;
    },
  },
  mounted() {},
  methods: {
    ...mediaMethods,
    cancelUploadLocal() {
      this.cancelUpload({ index: this.currentUploadIndex });
    },
    async onFileChange(evt) {
      this.timeStarted = new Date();
      this.timeInterval = setInterval(() => {
        if (
          this.currentUploadData !== undefined &&
          this.currentUploadData.file &&
          this.currentUploadData.file.size &&
          this.timeStarted
        ) {
          const totalBytes = this.currentUploadData.file.size;
          const uploadedBytes =
            this.currentUploadData.file.size *
            (this.currentUploadData.uploadProgress / 100);
          const timeElapsed = new Date() - this.timeStarted;
          const uploadSpeed = uploadedBytes / (timeElapsed / 1000);
          const timeRemaining = (totalBytes - uploadedBytes) / uploadSpeed;
          this.timeRemaining = timeRemaining;
        }
      }, 1000);

      var vid = document.createElement('video');
      var fileURL = URL.createObjectURL(evt.target.files[0]);
      vid.src = fileURL;
      this.videoError = null;
      // wait for duration to change from NaN to the actual duration
      vid.ondurationchange = async () => {
        this.videoLength = vid.duration;
        if (this.videoLength >= 62) {
          // Video is too long
          this.videoError = 'Video is too long. Limit videos to 60 seconds.';
        } else if (evt.target.files[0].size >= 500000000) {
          // File size larger than 500mbs
          this.videoError =
            'Video file is too large. Limit file size to 500mbs.';
        } else {
          const file = evt.target.files[0];

          if (file.type === 'video/quicktime') {
            this.videoError =
              'Video file type is invalid. Please select other type.';
          } else {
            this.videoType = file.type;
            await this.setIsNewMediaAsset(true);
            this.$emit('update-temp-video-url', URL.createObjectURL(file));
            console.log(this.videoType);
            await this.getS3UploadUrl({
              file: file,
              orgId: this.userOrgId,
              requestType: this.requestType || 'orgs',
              trainerId: this.trainerId || '0',
              type: 'video',
            }).catch((e) => console.log(e));

            await this.uploadPhoto({
              model: this.requestType,
              option: 'video',
            });
          }
        }
      };
    },
  },
  watch: {
    currentUploadProgress(val) {
      if (val === 100) {
        setTimeout(() => {
          clearInterval(this.timeInterval);
        }, 3000);
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="video-container">
      <div v-if="!videoSrc" class="video-placeholder-wrapper">
        <img :src="placeholder" />
      </div>
      <VideoPlayer v-else :options="videoOpts" :key="videoSrc ? videoSrc : 1" />
      <span v-if="currentUploadProgress > 0" class="progress">
        <div :style="styling"
          ><span class="progress-title">{{
            currentUploadProgress === 100 ? 'Uploaded' : 'Uploading'
          }}</span
          ><span class="progress-number"
            >{{ currentUploadProgress }}%</span
          ></div
        >
      </span>
    </div>
    <label v-if="title" for="file">{{ title }}</label>
    <div v-if="helperText" class="helper-text margin-bottom">
      {{ helperText }}
    </div>
    <input
      v-if="
        !hideButton && !(currentUploadProgress > 0 && !currentUploadSuccess)
      "
      type="file"
      class="input-video btn-secondary"
      name="file"
      accept="video/mp4,video/mov,video/webm,video/quicktime"
      @change="onFileChange"
      :disabled="currentUploadProgress > 0 && !currentUploadSuccess"
    />
    <BaseButton
      v-if="currentUploadProgress > 0 && !currentUploadSuccess"
      type="button"
      class="btn btn-secondary btn-danger"
      @click.prevent="cancelUploadLocal"
    >
      Cancel Upload
    </BaseButton>
    <p v-if="currentUploadProgress > 0" class="time-remaining"
      >About {{ duration }} remaining</p
    >
    <p class="helper-text text-danger" v-show="videoError">{{ videoError }}</p>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.video-placeholder-wrapper {
  display: block;
  margin-bottom: 2rem;
  width: 50%;

  img {
    width: 100%;
  }
}

.vjs-tech {
  height: 4rem !important;
}

.input-video {
  background-color: transparent;
  @extend %input-file-upload;

  &::before {
    @extend %typography-small;
    @extend %input-file-upload-btn;

    color: $color-action;
    content: 'Upload New Video';
  }

  &:hover {
    &::before {
      color: $color-off-white;
    }
  }
}

.video-container {
  position: relative;
}

.progress {
  align-items: center;
  background-color: rgba($color-white-wo-rgb, 0.5);
  bottom: 0;
  display: flex;
  height: 25%;
  position: absolute;
  width: 50%;
  div {
    align-items: center;
    display: flex;
    float: right;
    height: 100%;
    justify-content: space-around;
    .progress-title {
      color: white;
      font-style: normal;
      font-weight: bold;
      left: 1.5rem;
      position: absolute;
      @include calc-font-size(1.1);
    }
    .progress-number {
      color: white;
      font-style: normal;
      font-weight: bold;
      position: absolute;
      right: 1.5rem;
      @include calc-font-size(1.1);
    }
  }
}
.btn-danger {
  height: 3.9rem;
  width: 16rem;
}

.time-remaining {
  opacity: 0.5;
}
</style>
